import React, { useRef, useEffect, useState } from "react";
import LocationSearchCard from "./LocationSearchCard";
import { RecentLocation } from "./searchStorage";
import { AirportOption } from "./useAirportCodes";

interface AirportFieldProps {
  type: "from" | "to";
  label: string; // "From" / "To"
  selectedCode: string;
  displayValue: string;
  options: AirportOption[];
  recentLocations: RecentLocation[];
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  searchInput: string;
  setSearchInput: (value: string) => void;
  isLoading: boolean;
  onSearch: (input: string) => Promise<void> | void;
  onSelect: (code: string, name: string) => void;
  onClear: () => void;
  onClose: () => void;
  error?: string;
}

// A single Flying-From / Going-to field: the interactive search input + clear
// button, with the airport search dropdown popover anchored to it.
const AirportField: React.FC<AirportFieldProps> = ({
  type,
  label,
  selectedCode,
  displayValue,
  options,
  recentLocations,
  isOpen,
  setOpen,
  searchInput,
  setSearchInput,
  isLoading,
  onSearch,
  onSelect,
  onClear,
  onClose,
  error,
}) => {
  const mainInputRef = useRef<HTMLInputElement>(null);
  const isDirtyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isOpen && mainInputRef.current) {
      mainInputRef.current.focus();
    }
    if (!isOpen) {
      isDirtyRef.current = false;
      setIsDirty(false);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    isDirtyRef.current = true;
    setIsDirty(true);
    setSearchInput(val);
    if (!isOpen) setOpen(true);
    Promise.resolve(onSearch(val.trim())).catch((err) => {
      console.error("Error in onSearch:", err);
    });
  };

  const handleInputFocus = () => {
    if (!isOpen) setOpen(true);
    requestAnimationFrame(() => mainInputRef.current?.select());
  };

  const handleSelectLocation = (code: string, name: string) => {
    isDirtyRef.current = false;
    setIsDirty(false);
    onSelect(code, name);
  };

  const handleCardClose = () => {
    if (isDirtyRef.current && searchInput.trim() === "" && selectedCode) {
      onClear();
    } else {
      onClose();
    }
  };

  const hasValue = Boolean(selectedCode && !isOpen);
  const inputValue = isOpen && isDirty ? searchInput : displayValue;

  return (
    <div
      className="relative flex-1 h-full px-3 sm:px-4 cursor-pointer transition-colors hover:bg-gray-50 flex flex-col justify-center min-w-0"
      onClick={() => {
        if (!isOpen) setOpen(true);
      }}
    >
      {hasValue ? (
        /* Selected State: Stacked sub-header with small flight icon + label above selected city name */
        <div
          className={`flex flex-col justify-center min-w-0 py-1 ${
            error ? "" : "h-full"
          }`}
        >
          <div className="flex items-center gap-1 text-[#6B7280] mb-0.5">
            <i
              className="st-flight text-white text-[12px] inline-block shrink-0"
              style={{
                transform: type === "to" ? "rotate(50deg)" : "rotate(-50deg)",
                WebkitTextStroke: "0.6px #6B7280",
              }}
            ></i>
            <span className="text-[11px] sm:text-xs font-semibold font-inter text-[#6B7280] leading-none">
              {label}
            </span>
          </div>
          <div className="relative flex-1 flex items-center min-w-0">
            <input
              ref={mainInputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) setOpen(true);
              }}
              onFocus={handleInputFocus}
              placeholder={label}
              className="w-full p-0 border-none bg-transparent font-inter text-base sm:text-[17px] text-[#111827] font-semibold leading-tight focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-transparent truncate"
              style={{ outline: "none", boxShadow: "none" }}
              autoComplete="off"
            />
          </div>
        </div>
      ) : (
        /* Unselected / Active Search State: Single horizontal row with icon on left */
        <div
          className={`flex items-center gap-2 min-w-0 ${error ? "" : "h-full"}`}
        >
          <div className="shrink-0 flex items-center justify-center">
            <i
              className="st-flight text-white text-[21px] inline-block"
              style={{
                transform: type === "to" ? "rotate(50deg)" : "rotate(-50deg)",
                WebkitTextStroke: "0.7px #4B5563",
              }}
            ></i>
          </div>
          <div className="relative flex-1 flex items-center min-w-0 h-full">
            <input
              ref={mainInputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) setOpen(true);
              }}
              onFocus={handleInputFocus}
              placeholder={label}
              className="w-full p-0 pr-6 border-none bg-transparent font-inter text-base text-[#192227] font-normal leading-snug focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-transparent placeholder-[#6B7280] truncate"
              style={{ outline: "none", boxShadow: "none" }}
              autoComplete="off"
            />
          </div>
        </div>
      )}

      <LocationSearchCard
        type={type}
        value={selectedCode}
        onChange={handleSelectLocation}
        recentLocations={recentLocations}
        isOpen={isOpen}
        onOpen={() => setOpen(true)}
        onClose={handleCardClose}
        placeholder="Search airports..."
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        options={options}
        isLoading={isLoading}
        label={type === "from" ? "From" : "To"}
        onSearch={onSearch}
      />

      {/* Validation error text inside the field, below the input line */}
      {error && (
        <p
          title={error}
          className="mt-1 text-[10px] sm:text-[11px] text-red-500 font-medium leading-tight truncate pointer-events-none"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default AirportField;
